import { useCallback } from 'react';
import { useApolloClient, useMutation } from '@apollo/client/react';
import { toast } from 'react-toastify';
import {
  RemoveProjectDocument,
  LastProjectDocument,
  ProjectsDocument,
  type ProjectsQuery,
  type ProjectsQueryVariables,
} from 'graphql/generated/graphql';
import { messageOf } from 'utils/error';
import requireValue from 'utils/requireValue';
import useLastProjectId from './useLastProjectId';

const useRemoveProject = () => {
  const [removeProjectMutation] = useMutation(RemoveProjectDocument, {
    onCompleted: () => {},
  });
  const client = useApolloClient();
  const { lastProjectId } = useLastProjectId();

  const handleRemoveProject = useCallback(
    async (projectId: string) => {
      try {
        await removeProjectMutation({
          variables: { projectId },
          refetchQueries: [{ query: ProjectsDocument }],
          awaitRefetchQueries: true,
        });

        if (projectId === lastProjectId) {
          const { projects } = requireValue(
            client.readQuery<ProjectsQuery, ProjectsQueryVariables>({ query: ProjectsDocument }),
            'projects',
          );
          const lastProject = requireValue(projects[projects.length - 1], '_id');

          // `__typename` is not selected, so the generated type lacks it; the cache needs it to normalise the project.
          const lastProjectRef = { _id: lastProject._id, __typename: 'Project' };
          client.writeQuery({ query: LastProjectDocument, data: { lastProject: lastProjectRef } });
        }
      } catch (error) {
        toast.error(messageOf(error));
      }
    },
    [client, lastProjectId, removeProjectMutation],
  );
  return handleRemoveProject;
};

export default useRemoveProject;
